"use client"

import { SignInWithApple } from "@capacitor-community/apple-sign-in"
import type { SupabaseClient } from "@supabase/supabase-js"

// ============================================================
// Sign in with Apple, the native way.
// ------------------------------------------------------------
// Google's flow has to leave the app — Google refuses OAuth in a web view and
// answers `disallowed_useragent`. Apple is the opposite: on iOS it expects its
// OWN sheet (ASAuthorizationController), rendered by the system over the app,
// with Face ID and the Hide My Email choice. Routing it through a browser works
// but is slower, drops the person out of the app, and is the kind of thing a
// reviewer reads as a repackaged website under guideline 4.8.
//
// So Apple gets this, and Google keeps the browser round-trip.
//
// THE AUDIENCE CHANGES, WHICH IS THE PART THAT BREAKS SILENTLY. The web flow is
// brokered by Supabase, so its identity token is issued to the SERVICES ID
// (co.boothapp.auth). A token from the native sheet is issued to the APP ID
// (co.boothapp.app) — the bundle identifier. Supabase verifies the `aud` claim,
// so the Apple provider must list BOTH. In the dashboard that is the Apple
// provider's "Authorized Client IDs" field. With only the Services ID there,
// this path fails with "Unacceptable audience in id_token" and nothing about
// the message points at the dashboard.
//
// THE NONCE. We generate a random value, send Apple the SHA-256 of it, and give
// Supabase the raw one. Apple copies what it was given into the token's `nonce`
// claim; Supabase hashes the raw value and compares. The raw nonce therefore
// never leaves the device, and a token captured in flight cannot be replayed
// into a session without it.
// ============================================================

/** The Services ID. iOS ignores it — the native sheet uses the bundle id — but
 *  the plugin's web fallback needs a client id, and this is the right one. */
export const APPLE_SERVICES_ID = "co.boothapp.auth"

/**
 * ASAuthorizationError.canceled — the person tapped Cancel, which is not an
 * error and must not raise one.
 *
 * Deliberately narrow. "AuthorizationError" was in this list and matched the
 * name of EVERY error the framework raises, so a real failure (no entitlement,
 * wrong audience, no network) would have been swallowed as a silent cancel and
 * the button would have just stopped working with nothing on screen.
 */
const CANCELLED = ["1001", "canceled", "cancelled"]

export function randomNonce(bytes = 32): string {
  const buf = new Uint8Array(bytes)
  crypto.getRandomValues(buf)
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("")
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("")
}

export type AppleNativeResult =
  | { ok: true }
  | { ok: false; cancelled: true }
  | { ok: false; cancelled: false; message: string }

/**
 * Run the native sheet and exchange the result for a Supabase session.
 *
 * On success the session lives in cookies (the browser client is
 * @supabase/ssr's), so the caller can navigate to /auth/native and the route
 * handler will see it on the request.
 */
export async function signInWithAppleNative(supabase: SupabaseClient): Promise<AppleNativeResult> {
  const rawNonce = randomNonce()

  let identityToken: string | undefined
  let givenName: string | null | undefined
  let familyName: string | null | undefined

  try {
    const result = await SignInWithApple.authorize({
      clientId: APPLE_SERVICES_ID,
      // Unused by the native sheet; Apple requires the field to be present.
      redirectURI: `${window.location.origin}/auth/callback`,
      scopes: "email name",
      nonce: await sha256Hex(rawNonce),
    })
    identityToken = result.response?.identityToken
    givenName = result.response?.givenName
    familyName = result.response?.familyName
  } catch (err) {
    const text = `${(err as { code?: string })?.code ?? ""} ${(err as Error)?.message ?? ""}`
    if (CANCELLED.some((c) => text.toLowerCase().includes(c.toLowerCase()))) {
      return { ok: false, cancelled: true }
    }
    return { ok: false, cancelled: false, message: (err as Error)?.message || "Apple sign in failed." }
  }

  if (!identityToken) {
    // The sheet returned without a token. Nothing to exchange, and retrying is
    // the only sensible answer.
    return { ok: false, cancelled: false, message: "Apple didn't return a sign-in token. Please try again." }
  }

  const { error } = await supabase.auth.signInWithIdToken({
    provider: "apple",
    token: identityToken,
    nonce: rawNonce,
  })
  if (error) return { ok: false, cancelled: false, message: error.message }

  // Apple sends the name ONLY on the very first authorization for this Apple ID
  // and never again — not in the token, only in the authorization response. If
  // we drop it here the account has no name forever, and
  // lib/auth/complete-oauth-sign-in reads user_metadata.full_name to seed the
  // profile. So write it through before anything navigates.
  const fullName = [givenName, familyName].filter(Boolean).join(" ").trim()
  if (fullName) {
    const { data } = await supabase.auth.getUser()
    const existing = data.user?.user_metadata?.full_name || data.user?.user_metadata?.name
    if (!existing) {
      // Non-fatal: a nameless profile is recoverable in settings, a failed
      // sign-in is not.
      await supabase.auth.updateUser({ data: { full_name: fullName, name: fullName } }).catch(() => {})
    }
  }

  return { ok: true }
}
